// Branch types
export interface BranchInfo {
  id: string;
  code: string;
  name: string;
  isHeadOffice: boolean;
}

export interface BranchContext {
  currentBranch: BranchInfo | null;
  primaryBranchId: string | null;
  effectiveBranches: BranchInfo[];
  hasAllBranches: boolean;
}

// User & Auth types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  isActive: boolean;
  companyId: string;
  roles: string[];
  permissions: Permission[];
  avatarUrl?: string;
  currentBranchId?: string;
  currentBranchCode?: string;
  currentBranchName?: string;
  primaryBranchId?: string;
  hasAllBranches: boolean;
  effectiveBranches: BranchInfo[];
}

export interface Permission {
  module: string;
  resource: string;
  action: string;
}

export interface FieldPermission {
  module: string;
  resource: string;
  field: string;
  action: string;
}

export interface Company {
  id: string;
  name: string;
  code: string;
  currency: string;
  timezone: string;
  locale: string;
  logoUrl?: string;
  isActive: boolean;
}

export interface Notification {
  id: string;
  recipientUserId: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  actionUrl: string | null;
  module: string | null;
  createdAt: string;
  triggeredBy?: string | null;
  branchCode?: string | null;
}

export type Theme = 'light' | 'dark' | 'system';

// API types
export interface PagedRequest {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  search?: string;
}

export interface PagedResponse<T> {
  data: T[];
  meta: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

export interface ApiResponse<T> {
  data: T;
  success: boolean;
  errors: ApiError[];
}

export interface ApiError {
  code: string;
  message: string;
  field?: string;
}

// Module types
export const MODULE_NAMES = [
  'finance',
  'hr',
  'graphics',
  'tabang',
  'reporting',
  'vendor',
  'icdc',
  'legacyalign',
  'item-management',
  'inventory',
  'supplychain',
  'qhp',
  'ismc',
  'clearinghouse',
  'poscentral',
  'marketing',
  'controlcenter',
  'edge',
] as const;

export type ModuleName = (typeof MODULE_NAMES)[number];

export interface ModuleConfig {
  name: ModuleName;
  displayName: string;
  icon: string;
  basePath: string;
  remoteUrl: string;
  isEnabled: boolean;
}

// Menu layout types
export type MenuNode =
  | { type: 'module'; module: ModuleName }
  | { type: 'folder'; id: string; label: string; children: MenuNode[] };

export type MenuLayout = MenuNode[];

// Event bus types
export type EventPayload = Record<string, unknown>;

export interface EventBusEvent<T = EventPayload> {
  type: string;
  payload: T;
  timestamp: number;
  source: string;
}

// Tabang (Support Tickets) types
export type TicketPriority = 'Low' | 'Medium' | 'High' | 'Critical';
// TAB-0072: Done is no longer terminal — it's the assignee's "finished"
// claim. Resolved (reporter approved) and Closed (admin force-close) are.
export type TicketStatus = 'Open' | 'OnHold' | 'InProgress' | 'Done' | 'Resolved' | 'Closed';

export interface Department {
  id: string;
  name: string;
  code: string;
}

export interface Branch {
  id: string;
  name: string;
  code: string;
  address?: string;
}

export interface Ticket {
  id: string;
  ticketNumber: string;
  created: string;
  dueDate?: string;
  title: string;
  reporterName: string;
  ipAddress?: string;
  location?: string;
  assetTag?: string;
  description: string;
  priority: TicketPriority;
  status: TicketStatus;
  assigneeId?: string;
  assigneeName?: string;
  reporterId: string;
  departmentId?: string;
  departmentName?: string;
  branchId?: string;
  branchName?: string;
  attachments?: string[];
  updatedAt: string;
  // TAB-0072: the reporter rejected the assignee's most recent Done claim.
  isUnresolved?: boolean;
  // TAB-0072: why an admin force-closed. Set only when status === 'Closed'.
  closedReason?: string | null;
}

export interface CreateTicketDto {
  title: string;
  reporterName: string;
  ipAddress?: string;
  location?: string;
  assetTag?: string;
  description: string;
  priority: TicketPriority;
  dueDate?: string;
  departmentId?: string;
  branchId?: string;
  attachments?: File[];
}
